// Partners.js - Version simplifiée avec design épuré
import React, { useState, useEffect, useRef } from 'react';
import './Partners.css';

// Import des images disponibles
import societeGeneraleLogo from '../../assets/images/societegenerallogo.avif';
import studapartLogo from '../../assets/images/studapartlogo.avif';
import nexityLogo from '../../assets/images/nexitylogo.avif';
import smartGarantLogo from '../../assets/images/smartgarantlogo.avif';
import studelyLogo from '../../assets/images/studelylogo.avif';
import omnesEducationLogo from '../../assets/images/omneseducationlogo.avif';
import bnpparibasLogo from '../../assets/images/logobnparibas.jpg';
import collegeDeParisLogo from '../../assets/images/logocollegedeparis.jpg';
import ecopiaLogo from '../../assets/images/ecopialogo.jpeg';
import edhLogo from '../../assets/images/logoedh.png';

const Partners = () => {
  const partners = [
    { id: 1, name: "Société Générale", logo: societeGeneraleLogo, url: "#", color: "#1069d3" },
    { id: 2, name: "StudaPart", logo: studapartLogo, url: "#", color: "#1e3c72" },
    { id: 3, name: "Nexity", logo: nexityLogo, url: "#", color: "#00a8a8" },
    { id: 4, name: "Smart Garant", logo: smartGarantLogo, url: "#", color: "#ff6b35" },
    { id: 5, name: "Studely", logo: studelyLogo, url: "#", color: "#2a5298" },
    { id: 6, name: "Omnes Education", logo: omnesEducationLogo, url: "#", color: "#dc143c" },
    { id: 7, name: "BNP Paribas", logo: bnpparibasLogo, url: "#", color: "#00875A" },
    { id: 8, name: "Collège de Paris", logo: collegeDeParisLogo, url: "#", color: "#4A90E2" },
    { id: 9, name: "Ecopia", logo: ecopiaLogo, url: "#", color: "#2ecc71" },
    { id: 10, name: "EDH", logo: edhLogo, url: "#", color: "#FF6B00" }
  ];

  const [activeDot, setActiveDot] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollContentRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Calculer la largeur d'une carte + gap
  const getCardWidth = () => {
    if (window.innerWidth >= 1200) return 240 + 32;
    if (window.innerWidth >= 992) return 220 + 32;
    if (window.innerWidth >= 768) return 200 + 24;
    if (window.innerWidth >= 480) return 180 + 24;
    return 160 + 24;
  };

  const scrollLeft = () => {
    const container = scrollContentRef.current;
    if (!container) return;

    const cardWidth = getCardWidth();
    const newPosition = Math.max(scrollPosition - cardWidth * 3, 0);
    
    setScrollPosition(newPosition);
    container.style.transform = `translateX(-${newPosition}px)`;
    updateScrollIndicator(newPosition);
  };

  const scrollRight = () => {
    const container = scrollContentRef.current;
    if (!container) return;

    const cardWidth = getCardWidth();
    const totalWidth = partners.length * cardWidth;
    const containerWidth = scrollContainerRef.current?.offsetWidth || 0;
    const maxScrollLeft = Math.max(0, totalWidth - containerWidth);
    const newPosition = Math.min(scrollPosition + cardWidth * 3, maxScrollLeft);
    
    setScrollPosition(newPosition);
    container.style.transform = `translateX(-${newPosition}px)`;
    updateScrollIndicator(newPosition);
  };

  const updateScrollIndicator = (position) => {
    const cardWidth = getCardWidth();
    const totalWidth = partners.length * cardWidth;
    const containerWidth = scrollContainerRef.current?.offsetWidth || 0;
    const maxScrollLeft = Math.max(0, totalWidth - containerWidth);
    
    if (maxScrollLeft <= 0) {
      setActiveDot(0);
      return;
    }
    
    const scrollPercentage = position / maxScrollLeft;
    const dotCount = 3;
    const activeIndex = Math.floor(scrollPercentage * dotCount);
    setActiveDot(Math.min(activeIndex, dotCount - 1));
  };

  useEffect(() => {
    const handleResize = () => {
      updateScrollIndicator(scrollPosition);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [scrollPosition]);

  useEffect(() => {
    updateScrollIndicator(0);
  }, []);

  return (
    <section className="partners-section" id="partners">
      <div className="partners-bg-pattern"></div>
      
      <div className="partners-wrapper">
        <div className="partners-header">
          <span className="partners-badge">Nos Alliés</span>
          <h2 className="partners-title">
            Partenaires
            <span className="partners-title-highlight"> Prestigieux</span>
          </h2>
          <div className="partners-line"></div>
          <p className="partners-intro">
            Des collaborations solides avec les leaders du secteur
          </p>
        </div>

        <div className="partners-scroll-container" ref={scrollContainerRef}>
          <div className="scroll-arrows">
            <button 
              className="scroll-arrow prev"
              onClick={scrollLeft}
              disabled={scrollPosition <= 0}
              style={{ opacity: scrollPosition <= 0 ? 0.4 : 1 }}
            >
              <span className="arrow-icon">←</span>
            </button>
            <button 
              className="scroll-arrow next"
              onClick={scrollRight}
            >
              <span className="arrow-icon">→</span>
            </button>
          </div>

          <div className="scroll-container-inner">
            <div 
              className="partners-scroll-content"
              ref={scrollContentRef}
              style={{ transform: `translateX(-${scrollPosition}px)` }}
            >
              {partners.map((partner, index) => (
                <div 
                  className="partner-item-card" 
                  key={partner.id}
                  style={{ '--delay': `${index * 0.05}s` }}
                >
                  <div className="card-shine" />
                  
                  <a 
                    href={partner.url} 
                    className="partner-card-link"
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <div className="card-logo-box">
                      <div className="partner-logo-image">
                        {partner.logo ? (
                          <img 
                            src={partner.logo} 
                            alt={`Logo ${partner.name}`}
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.parentElement;
                              const fallbackDiv = document.createElement('div');
                              fallbackDiv.className = 'logo-fallback';
                              fallbackDiv.textContent = partner.name.substring(0, 2);
                              fallback.appendChild(fallbackDiv);
                            }}
                          />
                        ) : (
                          <div className="logo-fallback">
                            {partner.name.substring(0, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="card-partner-info">
                      <div className="name-underline" />
                      <h3 className="partner-name-text">{partner.name}</h3>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
          
          <div className="partners-controls">
            <div className="scroll-indicator">
              <span className="indicator-text">Navigation</span>
              <div className="indicator-dots">
                {[0, 1, 2].map((index) => (
                  <div 
                    key={index}
                    className={`indicator-dot ${activeDot === index ? 'active' : ''}`}
                    onClick={() => {
                      const cardWidth = getCardWidth();
                      const totalWidth = partners.length * cardWidth;
                      const containerWidth = scrollContainerRef.current?.offsetWidth || 0;
                      const maxScrollLeft = Math.max(0, totalWidth - containerWidth);
                      const targetPosition = (index / 2) * maxScrollLeft;
                      const boundedPosition = Math.min(Math.max(targetPosition, 0), maxScrollLeft);
                      setScrollPosition(boundedPosition);
                      if (scrollContentRef.current) {
                        scrollContentRef.current.style.transform = `translateX(-${boundedPosition}px)`;
                      }
                      setActiveDot(index);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Partners;